import { lowerCase, upperFirst } from 'lodash-es'

export function isValidGitHash(hash: string) {
  const shortHashRegex = /^[0-9a-f]{7}$/

  return shortHashRegex.test(hash)
}

export const pad = (num: number, trailingZeroes = false, length = 2) =>
  trailingZeroes ? String(num).padStart(length, '0') : String(num)

export const formatTime = (
  time: number,
  {
    format = 's',
    trailingZeroes = false,
  }:
    | {
        format: 'h:m:s' | 'm:s' | 's' | 'm:s:ms'
        trailingZeroes?: boolean
      }
    | undefined = {
    format: 's',
    trailingZeroes: false,
  },
) => {
  const hours = Math.floor(time / 3600)
  const minutes = Math.floor((time % 3600) / 60)
  const seconds = Math.floor(time % 60)

  if (format === 'h:m:s') {
    return `${pad(hours, trailingZeroes)}:${pad(minutes, trailingZeroes)}:${pad(seconds, trailingZeroes)}`
  }

  if (format === 'm:s') {
    const totalMinutes = Math.floor(time / 60)

    return `${pad(totalMinutes, trailingZeroes)}:${pad(seconds, trailingZeroes)}`
  }

  if (format === 'm:s:ms') {
    const totalMinutes = Math.floor(time / 60)
    const totalSeconds = Math.floor(time % 60)
    const milliseconds = Math.floor((time % 1) * 1000)

    return `${pad(totalMinutes, trailingZeroes)}:${pad(totalSeconds, trailingZeroes)}:${pad(milliseconds, trailingZeroes, 3)}`
  }

  // 'ss' format
  return pad(time)
}

export const getCommaSeparatedValues = (str: string) =>
  str.replace(/\s/g, ',').split(',').filter(Boolean)

export const getAllValidUrls = (text: string) =>
  getCommaSeparatedValues(text).filter(isValidHttpUrl)

export const isValidHttpUrl = (text: string) => {
  try {
    const url = new URL(text)

    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch (_error) {
    return false
  }
}

export const isUuid = (text: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)

export const getInitials = (name: string, maxLength = 2) =>
  name
    .split(' ')
    .map((part) => part[0].toUpperCase())
    .slice(0, maxLength)
    .join('')

/**
 * Formats a string with the correct singular or plural form based on count
 *
 * @param count The count to determine singular or plural
 * @param singular The singular form of the word
 * @param plural The plural form of the word
 * @returns Formatted string with the correct form
 *
 * @example
 * formatPlural(1, 'user', 'users') // returns "1 user"
 * formatPlural(5, 'user', 'users') // returns "5 users"
 */
export function formatPlural(
  count: number,
  singular: string,
  plural: string,
): string {
  return `${count} ${count === 1 ? singular : plural}`
}

/**
 * Formats a string with the correct singular or plural form based on count.
 * formatPluralSubject(1, 'class', 'classes') // returns "class".
 * formatPluralSubject(5, 'class', 'classes') // returns "classes".
 */
export const formatPluralSubject = (
  count: number,
  singular: string,
  plural: string,
) => (count === 1 ? singular : plural)

/**
 * Simple formatting for pluralization by adding 's' for plural.
 * formatPluralSimple(1, 'user') // returns "1 user".
 * formatPluralSimple(5, 'user') // returns "5 users".
 */
export const formatPluralSimple = (count: number, word: string) =>
  formatPlural(count, word, `${word}s`)

/**
 * Formats a string with the correct singular or plural form based on count
 * Uses the simple English rule of adding 's' for plural
 *
 * @param count The count to determine singular or plural
 * @param word The base word (singular form)
 * @returns Formatted string with the correct form
 *
 * @example
 * formatPluralSubjectSimple(1, 'user') // returns "user"
 * formatPluralSubjectSimple(5, 'user') // returns "users"
 */
export function formatPluralSubjectSimple(count: number, word: string): string {
  return formatPluralSubject(count, word, `${word}s`)
}

/**
 * Extracts the filename from a URL
 *
 * @param url The URL to extract filename from
 * @param options Configuration options
 * @param options.includeExtension Whether to include the file extension (default: false)
 * @param options.fallback Fallback value if no filename is found (default: undefined)
 * @returns The filename with or without extension, or fallback value
 *
 * @example
 * getFileNameFromUrl('https://example.com/path/file.pdf') // returns "file"
 * getFileNameFromUrl('https://example.com/path/file.pdf', { includeExtension: true }) // returns "file.pdf"
 * getFileNameFromUrl('https://example.com/path/my.config.json') // returns "my.config"
 * getFileNameFromUrl('https://example.com/path/my.config.json', { includeExtension: true }) // returns "my.config.json"
 * getFileNameFromUrl('https://example.com/path/') // returns undefined
 * getFileNameFromUrl('https://example.com/path/', { fallback: 'unknown' }) // returns "unknown"
 */
export const getFileNameFromUrl = (
  url: string,
  options: {
    includeExtension?: boolean
    fallback?: string
  } = {},
): string | undefined => {
  const { includeExtension = false, fallback } = options

  try {
    const urlObj = new URL(url)
    let { pathname } = urlObj

    // Remove trailing slash if present
    if (pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1)
    }

    // Get the last segment of the path
    const segments = pathname.split('/')
    const lastSegment = segments[segments.length - 1]

    // Return fallback if no filename found
    if (!lastSegment || lastSegment === '') {
      return fallback
    }

    // Decode any URL encoding
    const decodedFileName = decodeURIComponent(lastSegment)

    // Return filename with extension if requested
    if (includeExtension) {
      return decodedFileName
    }

    // Remove extension (everything after the last dot)
    const lastDotIndex = decodedFileName.lastIndexOf('.')

    if (lastDotIndex === -1 || lastDotIndex === 0) {
      // No extension found or dot is at the beginning (hidden file)
      return decodedFileName
    }

    return decodedFileName.substring(0, lastDotIndex)
  } catch (_error) {
    return fallback
  }
}

/**
 * Truncates a string in the middle, keeping the start and end visible.
 *
 * @param value The string to truncate
 * @param startChars Number of characters to keep at the start (default: 6)
 * @param endChars Number of characters to keep at the end (default: 4)
 * @returns Truncated string with ellipsis in the middle, or original if short enough
 *
 * @example
 * truncateMiddle('6b01d3e8f2a1b4c9d0e5f341') // returns "6b01d3...5341"
 * truncateMiddle('6b01d3e8f2a1b4c9d0e5f341', 8, 6) // returns "6b01d3e8...e5f341"
 */
export const truncateMiddle = (
  value: string,
  startChars = 6,
  endChars = 4,
): string => {
  if (value.length <= startChars + endChars) {
    return value
  }

  return `${value.slice(0, startChars)}...${value.slice(-endChars)}`
}

export const formatKeyToLabel = (key: string): string =>
  upperFirst(lowerCase(key))
